import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, buildODataUrl, Toast, StandardDataGrid, gridActionButton, newRecordButtonStyle, getODataRows, resolveODataTotalCount } from './listPageStyles';

const STOCKS_API = '/api/stocks';
const MOVEMENTS_API = '/api/stockmovements';

const MOVEMENT_TYPES = [
  { value: 0, label: 'Manuel Düzeltme' },
  { value: 1, label: 'Sipariş Çıkışı' },
  { value: 2, label: 'Fatura Çıkışı' },
  { value: 3, label: 'Mal Girişi' },
];

const EMPTY_MOVEMENT_FORM = {
  warehouseId: '',
  productId: '',
  deltaQuantity: '',
  movementType: '0',
  description: '',
  movementDate: '',
};

function unwrapLookupItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}

function formatNumber(value) {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return num.toLocaleString('tr-TR', { maximumFractionDigits: 4 });
}

function formatDateTime(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('tr-TR');
  } catch {
    return '-';
  }
}

function movementTypeLabel(value) {
  const found = MOVEMENT_TYPES.find(t => t.value === Number(value));
  return found ? found.label : String(value ?? '-');
}

export function StockPageLegacy() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [movementModal, setMovementModal] = useState(false);
  const [movementForm, setMovementForm] = useState(EMPTY_MOVEMENT_FORM);
  const [historyModal, setHistoryModal] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);

  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const vendorMap = useMemo(() => {
    const map = new Map();
    vendors.forEach(v => map.set(v.id, v));
    return map;
  }, [vendors]);

  const warehouseMap = useMemo(() => {
    const map = new Map();
    warehouses.forEach(w => map.set(w.id, w));
    return map;
  }, [warehouses]);

  const productMap = useMemo(() => {
    const map = new Map();
    products.forEach(p => map.set(p.id, p));
    return map;
  }, [products]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    Promise.all([
      fetch('/api/vendors/Select', { headers: authHeaders() }).then(r => r.ok ? r.json() : []),
      fetch('/api/warehouses/Select', { headers: authHeaders() }).then(r => r.ok ? r.json() : []),
      fetch('/api/products/Select', { headers: authHeaders() }).then(r => r.ok ? r.json() : []),
    ]).then(([v, w, p]) => {
      setVendors(unwrapLookupItems(v));
      setWarehouses(unwrapLookupItems(w));
      setProducts(unwrapLookupItems(p));
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let filterParams = '';
      const q = filterText.trim();
      if (q) {
        const ids = [];
        const lower = q.toLocaleLowerCase('tr-TR');
        warehouses.forEach(w => {
          const haystack = `${w.textId || ''} ${w.name || ''}`.toLocaleLowerCase('tr-TR');
          if (haystack.includes(lower)) ids.push(`WarehouseId eq ${w.id}`);
        });
        products.forEach(p => {
          const haystack = `${p.textId || ''} ${p.name || ''} ${p.shortName || ''}`.toLocaleLowerCase('tr-TR');
          if (haystack.includes(lower)) ids.push(`ProductId eq ${p.id}`);
        });
        vendors.forEach(v => {
          const haystack = `${v.textId || ''} ${v.name || ''}`.toLocaleLowerCase('tr-TR');
          if (haystack.includes(lower)) ids.push(`VendorId eq ${v.id}`);
        });
        if (ids.length === 0) {
          setRows([]);
          setTotalCount(0);
          return;
        }
        filterParams = `(${ids.join(' or ')})`;
      }

      const url = buildODataUrl(STOCKS_API, { pageSize, currentPage, sortField, sortDir, filterParams });
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Stok verisi alınamadı');
      const data = await res.json();
      const pageRows = getODataRows(data);
      setRows(pageRows);
      setTotalCount(await resolveODataTotalCount({ data, rows: pageRows, currentPage, pageSize, base: STOCKS_API, filterParams, headers: authHeaders() }));
    } catch (error) {
      showToast('error', error.message || 'Veri alınırken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [filterText, sortField, sortDir, pageSize, currentPage, vendors, warehouses, products]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterSearch = () => {
    setFilterText(filterInput);
    setCurrentPage(1);
  };

  const clearFilter = () => {
    setFilterText('');
    setFilterInput('');
    setCurrentPage(1);
  };

  const labelFor = useCallback((map, id, fallback = '-') => {
    const item = map.get(id);
    if (!item) return fallback;
    if (item.name) return `${item.textId || ''} - ${item.name}`.replace(/^- /, '');
    return item.textId || fallback;
  }, []);

  const openNewMovement = (row = null) => {
    setSelected(row);
    setMovementForm({
      ...EMPTY_MOVEMENT_FORM,
      warehouseId: row?.warehouseId || '',
      productId: row?.productId || '',
    });
    setMovementModal(true);
  };

  const closeMovementModal = () => {
    setMovementModal(false);
    setMovementForm(EMPTY_MOVEMENT_FORM);
  };

  const handleSaveMovement = async () => {
    if (!movementForm.warehouseId) { showToast('error', 'Depo seçiniz'); return; }
    if (!movementForm.productId) { showToast('error', 'Ürün seçiniz'); return; }
    const delta = Number(movementForm.deltaQuantity);
    if (Number.isNaN(delta) || delta === 0) {
      showToast('error', 'Geçerli bir miktar giriniz (0 olamaz)');
      return;
    }
    const movementType = Number(movementForm.movementType);

    const payload = {
      warehouseId: movementForm.warehouseId,
      productId: movementForm.productId,
      deltaQuantity: delta,
      movementType,
      description: movementForm.description || null,
      movementDate: movementForm.movementDate || null,
    };

    setSaving(true);
    try {
      const res = await fetch(`${MOVEMENTS_API}/Add`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || 'Hareket kaydedilemedi');
      }
      showToast('success', 'Stok hareketi kaydedildi');
      closeMovementModal();
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (row) => {
    setSelected(row);
    setHistoryModal(true);
    setHistoryLoading(true);
    setHistory([]);
    try {
      const res = await fetch(`${MOVEMENTS_API}/GetByStock?StockId=${row.id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Hareket geçmişi alınamadı');
      const body = await res.json();
      setHistory(unwrapLookupItems(body));
    } catch (error) {
      showToast('error', error.message || 'Geçmiş yüklenirken hata oluştu');
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryModal(false);
    setHistory([]);
  };

  const columns = [
    {
      field: 'vendorLabel',
      headerName: 'Tedarikçi',
      flex: 1.1,
      minWidth: 180,
      renderCell: (params) => labelFor(vendorMap, params.row.vendorId),
    },
    {
      field: 'warehouseLabel',
      headerName: 'Depo',
      flex: 1.1,
      minWidth: 180,
      renderCell: (params) => labelFor(warehouseMap, params.row.warehouseId),
    },
    {
      field: 'productLabel',
      headerName: 'Ürün',
      flex: 1.4,
      minWidth: 220,
      renderCell: (params) => labelFor(productMap, params.row.productId),
    },
    {
      field: 'quantityInSmallestUnit',
      headerName: 'Miktar (En Küçük Birim)',
      width: 200,
      renderCell: (params) => {
        const value = params.row.quantityInSmallestUnit;
        const unit = productMap.get(params.row.productId)?.unit1 || '';
        return (
          <span style={{ fontWeight: 600, color: Number(value) < 0 ? '#E53E3E' : 'var(--text-primary)' }}>
            {formatNumber(value)} {unit}
          </span>
        );
      },
    },
    {
      field: 'updatedDate',
      headerName: 'Son Güncelleme',
      width: 180,
      renderCell: (params) => formatDateTime(params.row.updatedDate),
    },
    {
      field: 'actions',
      headerName: 'İşlemler',
      width: 230,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: '100%' }}>
          <button style={{ ...gridActionButton, background: 'var(--accent)' }} onClick={(e) => { e.stopPropagation(); openHistory(params.row); }}>Hareketler</button>
          <button style={{ ...gridActionButton, background: '#10b981' }} onClick={(e) => { e.stopPropagation(); openNewMovement(params.row); }}>+ Hareket</button>
        </div>
      ),
    },
  ];

  const productUnit = productMap.get(movementForm.productId)?.unit1 || '';

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h2 style={s.pageTitle}>Stok Yönetimi</h2>
          <p style={s.pageSubtitle}>Tedarikçi, depo ve ürün bazında stok bakiyeleri. Mutasyonlar hareket olarak kaydedilir.</p>
        </div>
        <button style={newRecordButtonStyle} onClick={() => openNewMovement()}>+ Yeni Hareket</button>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <input
            style={s.filterInput}
            type="text"
            placeholder="Tedarikçi, depo veya ürün adı/kodu ile filtrele..."
            value={filterInput}
            onChange={e => setFilterInput(e.target.value)}
            onKeyDown={event => { if (event.key === 'Enter') handleFilterSearch(); }}
          />
          <button style={s.btnSecondary} onClick={handleFilterSearch}>Ara</button>
          {filterText && <button style={s.btnSecondary} onClick={clearFilter}>Temizle</button>}
          <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
        </div>
      </div>

      <StandardDataGrid
        rows={rows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={page => setCurrentPage(page)}
        onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, direction) => { setCurrentPage(1); setSortField(field || 'UpdatedDate'); setSortDir(direction || 'desc'); }}
        selectedId={selected?.id}
        onRowClick={row => setSelected(row)}
        onRowDoubleClick={row => openHistory(row)}
      />

      {movementModal && (
        <div style={s.overlay} onClick={closeMovementModal}>
          <div style={{ ...s.modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Yeni Stok Hareketi</h3>
              <button style={s.closeBtn} onClick={closeMovementModal}>x</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Depo *</label>
                  <select
                    style={s.select}
                    value={movementForm.warehouseId}
                    onChange={e => setMovementForm(f => ({ ...f, warehouseId: e.target.value }))}
                  >
                    <option value="">Seçiniz</option>
                    {warehouses.map(w => (
                      <option key={w.id} value={w.id}>
                        {w.textId}{w.name ? ` - ${w.name}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Ürün *</label>
                  <select
                    style={s.select}
                    value={movementForm.productId}
                    onChange={e => setMovementForm(f => ({ ...f, productId: e.target.value }))}
                  >
                    <option value="">Seçiniz</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.textId}{p.name ? ` - ${p.name}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Hareket Tipi *</label>
                  <select
                    style={s.select}
                    value={movementForm.movementType}
                    onChange={e => setMovementForm(f => ({ ...f, movementType: e.target.value }))}
                  >
                    {MOVEMENT_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Miktar * (+/- en küçük birim{productUnit ? ` — ${productUnit}` : ''})</label>
                  <input
                    style={s.input}
                    type="number"
                    step="any"
                    value={movementForm.deltaQuantity}
                    onChange={e => setMovementForm(f => ({ ...f, deltaQuantity: e.target.value }))}
                    placeholder="Örn. 100 (giriş) veya -10 (çıkış)"
                  />
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Hareket Tarihi (boş bırakılırsa şu an)</label>
                  <input
                    style={s.input}
                    type="datetime-local"
                    value={movementForm.movementDate}
                    onChange={e => setMovementForm(f => ({ ...f, movementDate: e.target.value }))}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Açıklama</label>
                  <input
                    style={s.input}
                    value={movementForm.description}
                    onChange={e => setMovementForm(f => ({ ...f, description: e.target.value }))}
                    maxLength={500}
                    placeholder="Opsiyonel"
                  />
                </div>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnPrimary} onClick={handleSaveMovement} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
              <button style={s.btnSecondary} onClick={closeMovementModal}>İptal</button>
            </div>
          </div>
        </div>
      )}

      {historyModal && (
        <div style={s.overlay} onClick={closeHistory}>
          <div style={{ ...s.modal, maxWidth: 960 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                Hareket Geçmişi — {labelFor(productMap, selected?.productId)} @ {labelFor(warehouseMap, selected?.warehouseId)}
              </h3>
              <button style={s.closeBtn} onClick={closeHistory}>x</button>
            </div>
            <div style={s.modalBody}>
              {historyLoading ? (
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Yükleniyor...</p>
              ) : history.length === 0 ? (
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Bu stok için hareket kaydı yok.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left' }}>
                      <th style={{ padding: '8px 6px', fontSize: 13 }}>Tarih</th>
                      <th style={{ padding: '8px 6px', fontSize: 13 }}>Tip</th>
                      <th style={{ padding: '8px 6px', fontSize: 13, textAlign: 'right' }}>Miktar</th>
                      <th style={{ padding: '8px 6px', fontSize: 13 }}>Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(h => (
                      <tr key={h.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '8px 6px', fontSize: 13 }}>{formatDateTime(h.movementDate)}</td>
                        <td style={{ padding: '8px 6px', fontSize: 13 }}>{movementTypeLabel(h.movementType)}</td>
                        <td style={{ padding: '8px 6px', fontSize: 13, textAlign: 'right', fontWeight: 600, color: Number(h.deltaQuantity) < 0 ? '#E53E3E' : '#10b981' }}>
                          {Number(h.deltaQuantity) > 0 ? '+' : ''}{formatNumber(h.deltaQuantity)}
                        </td>
                        <td style={{ padding: '8px 6px', fontSize: 13, color: 'var(--text-secondary)' }}>{h.description || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnPrimary} onClick={() => { closeHistory(); openNewMovement(selected); }}>+ Yeni Hareket Ekle</button>
              <button style={s.btnSecondary} onClick={closeHistory}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
